/**
 * Next offset for an infinite report query, or undefined when there is no next page.
 * `loaded` = rows accumulated so far; `totalCount` = full filtered-set size (null when the backend
 * omitted `meta.pagination.total_count`); `lastPageLen`/`pageSize` are the authoritative end-of-stream
 * signal — a short or empty last page means the server has no more rows, even if a stale/over-counted
 * `totalCount` still reads higher. Returns undefined when the total is unknown (pagination disabled),
 * when the last page was short, or when everything is loaded — so "Show more" can never refetch a page
 * that adds nothing (the backend treats offset >= total_count as a valid-but-empty 200).
 */
export function nextReportOffset(
  loaded: number,
  totalCount: number | null,
  lastPageLen: number,
  pageSize: number,
): number | undefined {
  if (totalCount === null || lastPageLen < pageSize) {
    return undefined
  }
  return loaded < totalCount ? loaded : undefined
}

/**
 * Footer caption for a report list. Paginated mode (total known) shows a truthful running count;
 * degraded mode (total null, e.g. an environment that hasn't shipped totals) falls back to the legacy
 * "first N — export for the full set" note, but only when the page is full (otherwise there is nothing
 * to caption).
 */
export function reportCaption(loaded: number, totalCount: number | null, pageSize: number): string | undefined {
  if (totalCount !== null) {
    return `Showing ${loaded} of ${totalCount}`
  }
  return loaded >= pageSize ? `Showing first ${pageSize} — export for the full set.` : undefined
}
